import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function ConfirmedBookings() {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [transportComments, setTransportComments] = useState('');
  const [stayComments, setStayComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingComments, setEditingComments] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [b2bCost, setB2bCost] = useState('');
  const [savingB2b, setSavingB2b] = useState(false);
  const [miscPayment, setMiscPayment] = useState('');
  const [savingMisc, setSavingMisc] = useState(false);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/bookings/confirmed/list');
      setBookings(data);
    } catch (err) {
      toast.error('Failed to load confirmed bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (booking) => {
    setSelectedBooking(booking);
    setTransportComments(booking.transportComments || '');
    setStayComments(booking.stayComments || '');
    setEditingComments(!(booking.transportComments || booking.stayComments));
    setB2bCost(booking.b2bCost !== undefined ? String(booking.b2bCost) : '');
    setMiscPayment(booking.miscPaymentReceived !== undefined ? String(booking.miscPaymentReceived) : '');
  };

  const handleSaveComments = async () => {
    if (!selectedBooking) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/bookings/${selectedBooking.id}/comments`, {
        transportComments,
        stayComments
      });
      setSelectedBooking(data);
      setBookings(prev => prev.map(b => b.id === data.id ? data : b));
      setEditingComments(false);
      toast.success('Comments saved!');
    } catch (err) {
      toast.error('Failed to save comments');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalizePayment = async () => {
    if (!selectedBooking) return;
    const totalCost = selectedBooking.pricing?.totalCost || 0;
    const input = window.prompt(`Enter the final amount paid by ${selectedBooking.customerName}:`, String(totalCost));
    if (input === null) return;
    const amount = Number(input);
    if (isNaN(amount) || amount < 0) return toast.error('Enter a valid amount');
    if (!window.confirm(`Mark all payments as complete for ${selectedBooking.customerName} with ₹${amount.toLocaleString('en-IN')} received? This cannot be undone.`)) return;
    setFinalizing(true);
    try {
      const { data } = await api.patch(`/bookings/${selectedBooking.id}/finalize-payment`, { finalAmountReceived: amount });
      setSelectedBooking(data);
      setBookings(prev => prev.map(b => b.id === data.id ? data : b));
      toast.success('All payments finalized!');
    } catch (err) {
      toast.error('Failed to finalize payment');
    } finally {
      setFinalizing(false);
    }
  };

  const handleSaveB2bCost = async () => {
    if (!selectedBooking) return;
    setSavingB2b(true);
    try {
      const { data } = await api.patch(`/bookings/${selectedBooking.id}/b2b-cost`, { b2bCost: Number(b2bCost) || 0 });
      setSelectedBooking(data);
      setBookings(prev => prev.map(b => b.id === data.id ? data : b));
      toast.success('B2B cost saved!');
    } catch (err) {
      toast.error('Failed to save B2B cost');
    } finally {
      setSavingB2b(false);
    }
  };

  const handleSaveMiscPayment = async () => {
    if (!selectedBooking) return;
    setSavingMisc(true);
    try {
      const { data } = await api.patch(`/bookings/${selectedBooking.id}/misc-payment`, { miscPaymentReceived: Number(miscPayment) || 0 });
      setSelectedBooking(data);
      setBookings(prev => prev.map(b => b.id === data.id ? data : b));
      toast.success('Misc payment recorded!');
    } catch (err) {
      toast.error('Failed to save misc payment');
    } finally {
      setSavingMisc(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filteredBookings = bookings.filter(b =>
    b.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.trekName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const upcomingBookings = filteredBookings
    .filter(b => b.startDate && new Date(b.startDate) >= today)
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  const pastBookings = filteredBookings
    .filter(b => !b.startDate || new Date(b.startDate) < today)
    .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">✅ Confirmed Bookings</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar - Booking List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 focus:ring-2 focus:ring-primary-500 outline-none"
              placeholder="Search bookings..." />

            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {loading ? (
                <p className="text-center text-gray-400 py-8">Loading...</p>
              ) : filteredBookings.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No confirmed bookings found</p>
              ) : (
                <>
                  {upcomingBookings.length > 0 && (
                    <>
                      <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide px-1 pt-1">
                        🗓️ Upcoming ({upcomingBookings.length})
                      </p>
                      {upcomingBookings.map(booking => {
                        const daysAway = Math.ceil((new Date(booking.startDate) - today) / (1000 * 60 * 60 * 24));
                        return (
                          <button key={booking.id} onClick={() => handleSelect(booking)}
                            className={`w-full text-left p-3 rounded-lg border text-sm transition ${
                              selectedBooking?.id === booking.id ? 'bg-primary-50 border-primary-300' : 'hover:bg-emerald-50 border-emerald-100'
                            }`}>
                            <div className="flex items-start justify-between gap-1">
                              <p className="font-medium text-gray-900">{booking.customerName}</p>
                              <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                {daysAway === 0 ? 'Today' : `${daysAway}d`}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">{booking.id} • {booking.trekName}</p>
                            <p className="text-xs text-gray-400 mt-1">{formatDate(booking.startDate)} – {formatDate(booking.endDate)}</p>
                          </button>
                        );
                      })}
                    </>
                  )}
                  {pastBookings.length > 0 && (
                    <>
                      {upcomingBookings.length > 0 && (
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1 pt-2">
                          Past Bookings ({pastBookings.length})
                        </p>
                      )}
                      {pastBookings.map(booking => (
                        <button key={booking.id} onClick={() => handleSelect(booking)}
                          className={`w-full text-left p-3 rounded-lg border text-sm transition ${
                            selectedBooking?.id === booking.id ? 'bg-primary-50 border-primary-300' : 'hover:bg-gray-50 border-gray-100'
                          }`}>
                          <p className="font-medium text-gray-900">{booking.customerName}</p>
                          <p className="text-xs text-gray-500">{booking.id} • {booking.trekName}</p>
                          <p className="text-xs text-gray-400 mt-1">{formatDate(booking.startDate)} – {formatDate(booking.endDate)}</p>
                        </button>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main - Booking Details */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border p-6">
            {selectedBooking ? (
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{selectedBooking.customerName}</h2>
                    <p className="text-sm text-gray-500">{selectedBooking.id}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    selectedBooking.paymentStatus === 'fully_paid' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {selectedBooking.paymentStatus === 'fully_paid' ? 'Fully Paid' : 'Advance Paid'}
                  </span>
                </div>

                {/* Booking Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">🏔️ Trek Details</h3>
                    <p className="text-sm"><span className="text-gray-500">Trek:</span> {selectedBooking.trekName}</p>
                    <p className="text-sm"><span className="text-gray-500">Duration:</span> {selectedBooking.duration || 'N/A'}</p>
                    <p className="text-sm"><span className="text-gray-500">Dates:</span> {formatDate(selectedBooking.startDate)} – {formatDate(selectedBooking.endDate)}</p>
                    <p className="text-sm"><span className="text-gray-500">Group Size:</span> {selectedBooking.groupSize}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">👤 Contact</h3>
                    <p className="text-sm"><span className="text-gray-500">Name:</span> {selectedBooking.customerName}</p>
                    <p className="text-sm"><span className="text-gray-500">Phone:</span> {selectedBooking.contactNumber || 'N/A'}</p>
                    <p className="text-sm"><span className="text-gray-500">Created by:</span> {selectedBooking.createdByName || selectedBooking.createdBy}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">📍 Pickup & Drop</h3>
                    <p className="text-sm"><span className="text-gray-500">Pickup:</span> {selectedBooking.pickupPoint?.name || 'N/A'} ({selectedBooking.pickupPoint?.time || ''})</p>
                    <p className="text-sm"><span className="text-gray-500">Drop:</span> {selectedBooking.dropoffPoint?.name || 'N/A'} ({selectedBooking.dropoffPoint?.time || ''})</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">💰 Pricing</h3>
                    <p className="text-sm"><span className="text-gray-500">Total Cost:</span> ₹{selectedBooking.pricing?.totalCost?.toLocaleString('en-IN')}</p>
                    <p className="text-sm"><span className="text-gray-500">Amount Paid:</span> ₹{(selectedBooking.paymentStatus === 'fully_paid' ? (selectedBooking.pricing?.finalAmountReceived ?? selectedBooking.pricing?.totalCost) : (selectedBooking.pricing?.advanceReceived ?? selectedBooking.pricing?.totalAdvance ?? 0))?.toLocaleString('en-IN')}</p>
                    <p className="text-sm"><span className="text-gray-500">Misc Charges:</span> ₹{(selectedBooking.pricing?.miscCharges || 0).toLocaleString('en-IN')}</p>
                    <p className="text-sm"><span className="text-gray-500">Balance:</span> ₹{selectedBooking.pricing?.totalPending?.toLocaleString('en-IN')}</p>
                    <p className="text-sm"><span className="text-gray-500">Payment Mode:</span> {selectedBooking.paymentMode}</p>
                  </div>
                </div>

                {/* Misc Payment Received */}
                {(selectedBooking.pricing?.miscCharges > 0) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">📝 Misc Payment Received</h3>
                    <div className="flex items-center gap-3 mb-2">
                      <label className="text-sm text-gray-500 whitespace-nowrap">Amount Received (₹):</label>
                      <input
                        type="number"
                        value={miscPayment}
                        onChange={(e) => setMiscPayment(e.target.value)}
                        className="border rounded px-3 py-1.5 text-sm w-40"
                        placeholder="Enter amount"
                      />
                      <button
                        onClick={handleSaveMiscPayment}
                        disabled={savingMisc}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                      >
                        {savingMisc ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                    {selectedBooking.miscPaymentReceived !== undefined && (
                      <div className="flex gap-6 mt-2">
                        <p className="text-sm"><span className="text-gray-500">Misc Charges:</span> <span className="font-medium">₹{(selectedBooking.pricing?.miscCharges || 0).toLocaleString('en-IN')}</span></p>
                        <p className="text-sm"><span className="text-gray-500">Received:</span> <span className="font-medium text-green-700">₹{selectedBooking.miscPaymentReceived?.toLocaleString('en-IN')}</span></p>
                        <p className="text-sm"><span className="text-gray-500">Misc Pending:</span> <span className="font-medium text-red-600">₹{Math.max(0, (selectedBooking.pricing?.miscCharges || 0) - (selectedBooking.miscPaymentReceived || 0)).toLocaleString('en-IN')}</span></p>
                      </div>
                    )}
                  </div>
                )}

                {/* B2B Cost & Profit Margin */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">📊 B2B Cost & Profit</h3>
                  <div className="flex items-center gap-3 mb-2">
                    <label className="text-sm text-gray-500 whitespace-nowrap">B2B Cost (₹):</label>
                    <input
                      type="number"
                      value={b2bCost}
                      onChange={(e) => setB2bCost(e.target.value)}
                      className="border rounded px-3 py-1.5 text-sm w-40"
                      placeholder="Enter B2B cost"
                    />
                    <button
                      onClick={handleSaveB2bCost}
                      disabled={savingB2b}
                      className="px-3 py-1.5 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700 disabled:opacity-50"
                    >
                      {savingB2b ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  {selectedBooking.b2bCost !== undefined && (
                    <div className="flex gap-6 mt-2">
                      <p className="text-sm"><span className="text-gray-500">B2B Cost:</span> <span className="font-medium">₹{selectedBooking.b2bCost?.toLocaleString('en-IN')}</span></p>
                      <p className="text-sm"><span className="text-gray-500">Profit Margin:</span> <span className="font-medium text-green-700">₹{selectedBooking.profitMargin?.toLocaleString('en-IN')}</span></p>
                    </div>
                  )}
                </div>

                {/* Group Members */}
                {selectedBooking.groupMembers && selectedBooking.groupMembers.length > 1 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">👥 Group Members</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedBooking.groupMembers.map((member, i) => (
                        <span key={i} className="px-2 py-1 bg-white border rounded text-xs">{member}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Editable Comments */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">📝 Comments</h3>
                    {!editingComments && (selectedBooking.transportComments || selectedBooking.stayComments) && (
                      <button onClick={() => setEditingComments(true)}
                        className="text-blue-600 text-sm font-medium hover:underline">✏️ Edit</button>
                    )}
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Transport Comments</label>
                      {editingComments ? (
                        <textarea value={transportComments} onChange={e => setTransportComments(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm resize-none"
                          rows={3} placeholder="Add transport-related comments..." />
                      ) : (
                        <p className="px-3 py-2 bg-gray-50 border rounded-lg text-sm text-gray-700 min-h-[40px]">
                          {selectedBooking.transportComments || <span className="text-gray-400 italic">No comments</span>}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Stay Comments</label>
                      {editingComments ? (
                        <textarea value={stayComments} onChange={e => setStayComments(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm resize-none"
                          rows={3} placeholder="Add stay/accommodation-related comments..." />
                      ) : (
                        <p className="px-3 py-2 bg-gray-50 border rounded-lg text-sm text-gray-700 min-h-[40px]">
                          {selectedBooking.stayComments || <span className="text-gray-400 italic">No comments</span>}
                        </p>
                      )}
                    </div>
                    {editingComments && (
                      <div className="flex gap-2">
                        <button onClick={handleSaveComments} disabled={saving}
                          className="bg-primary-600 text-white px-6 py-2 rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50 transition">
                          {saving ? 'Saving...' : '💾 Save Comments'}
                        </button>
                        {(selectedBooking.transportComments || selectedBooking.stayComments) && (
                          <button onClick={() => { setEditingComments(false); setTransportComments(selectedBooking.transportComments || ''); setStayComments(selectedBooking.stayComments || ''); }}
                            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-200 transition">
                            Cancel
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Finalize Payment */}
                {selectedBooking.paymentStatus !== 'fully_paid' && (
                  <div className="border-t pt-6">
                    <button onClick={handleFinalizePayment} disabled={finalizing}
                      className="bg-green-600 text-white px-6 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 transition font-medium">
                      {finalizing ? 'Finalizing...' : '✅ Finalize All Payments'}
                    </button>
                    <p className="text-xs text-gray-400 mt-2">Click when all payments for this booking have been received in full.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16 text-gray-400">
                <p className="text-5xl mb-4">📋</p>
                <p className="text-lg">Select a booking to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
